import React, { Component } from "react";
import PropTypes from "prop-types";
import { shallowEqual } from "recompose";
import Dropzone from "react-dropzone";
import compose from "recompose/compose";
import Chip from "@material-ui/core/Chip";

import FileIcon from "@material-ui/icons/InsertDriveFile";
import DeleteIcon from "@material-ui/icons/Cancel";

import { withStyles } from "@material-ui/core/styles";
import FormHelperText from "@material-ui/core/FormHelperText";

import classnames from "classnames";

import { translate, Labeled, withFormField } from "jazasoft";

const MyFileInputPreview = props => {
  const { children, className, onRemove, revokeObjectURL, file, chipProps, ...rest } = props;
  const { rawFile = {} } = file;
  return (
    <div className={className} {...rest}>
      <Chip
        label={rawFile.name}
        icon={<FileIcon />}
        deleteIcon={<DeleteIcon />}
        variant="outlined"
        color="primary"
        component="a"
        clickable
        target="_blank"
        rel="noopener noreferrer"
        onDelete={onRemove}
        {...chipProps}
      />
    </div>
  );
};

const sanitizeRestProps = ({
  alwaysOn,
  basePath,
  component,
  defaultValue,
  formClassName,
  initializeForm,
  input,
  isRequired,
  label,
  locale,
  meta,
  options,
  optionText,
  optionValue,
  idKey,
  record,
  resource,
  i18nKey,
  referenceResource,
  parentResource,
  allowEmpty,
  source,
  textAlign,
  translate,
  translateChoice,
  ...rest
}) => rest;

const styles = {
  dropZone: {
    background: "#efefef",
    cursor: "pointer",
    padding: "1rem",
    textAlign: "center",
    color: "#999",
    minHeight: "3em"
  },
  preview: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap"
  },
  chips: {
    padding: "0.5em"
  },
  root: { width: "100%" },
  paddingTop: {
    paddingTop: "1em"
  }
};

export class MyFileInput extends Component {
  static propTypes = {
    accept: PropTypes.object,
    classes: PropTypes.object,
    className: PropTypes.string,
    disableClick: PropTypes.bool,
    id: PropTypes.string,
    input: PropTypes.object,
    isRequired: PropTypes.bool,
    label: PropTypes.string,
    labelMultiple: PropTypes.string,
    labelSingle: PropTypes.string,
    maxSize: PropTypes.number,
    minSize: PropTypes.number,
    multiple: PropTypes.bool,
    options: PropTypes.object,
    resource: PropTypes.string,
    source: PropTypes.string,
    translate: PropTypes.func.isRequired,
    placeholder: PropTypes.node
  };

  static defaultProps = {
    labelMultiple: "js.input.file.upload_several",
    labelSingle: "js.input.file.upload_single",
    multiple: false,
    onUpload: () => {},
    translate: id => id
  };

  constructor(props) {
    super(props);
    let files = props.input.value || [];
    if (!Array.isArray(files)) {
      files = [files];
    }

    this.state = {
      files: files.map(this.transformFile)
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    let files = nextProps.input.value || [];
    if (!Array.isArray(files)) {
      files = [files];
    }

    this.setState({ files: files.map(this.transformFile) });
  }

  onDrop = files => {
    const updatedFiles = this.props.multiple ? [...this.state.files, ...files.map(this.transformFile)] : [...files.map(this.transformFile)];

    this.setState({ files: updatedFiles });

    if (this.props.multiple) {
      // Use onBlur to ensure redux-form set the input as touched
      this.props.input.onBlur(updatedFiles);
    } else {
      this.props.input.onBlur(updatedFiles[0]);
    }
  };

  onRemove = file => () => {
    const filteredFiles = this.state.files.filter(stateFile => !shallowEqual(stateFile, file));

    this.setState({ files: filteredFiles });

    // Use onBlur to ensure redux-form set the input as touched
    if (this.props.multiple) {
      this.props.input.onBlur(filteredFiles);
    } else {
      this.props.input.onBlur(null);
    }
  };

  // turn a browser dropped file structure into expected structure
  transformFile = file => {
    if (!(file instanceof File)) {
      return file;
    }

    const transformedFile = {
      rawFile: file
    };

    return transformedFile;
  };

  label() {
    const { translate, placeholder, labelMultiple, labelSingle } = this.props;

    if (placeholder) {
      return placeholder;
    }

    if (this.props.multiple) {
      return <p>{translate(labelMultiple)}</p>;
    }

    return <p>{translate(labelSingle)}</p>;
  }

  render() {
    const {
      accept,
      classes = {},
      className,
      disableClick,
      id,
      isRequired,
      label,
      maxSize,
      meta,
      minSize,
      multiple,
      resource,
      source,
      translate,
      options = {},
      chipProps,
      ...rest
    } = this.props;

    const { files = [] } = this.state;

    return (
      <Labeled
        id={id}
        label={label}
        className={classnames(classes.root, className)}
        source={source}
        resource={resource}
        isRequired={isRequired}
        meta={meta}
        {...sanitizeRestProps(rest)}>
        <span>
          <Dropzone onDrop={this.onDrop} accept={accept} maxSize={maxSize} minSize={minSize} multiple={multiple} noClick={disableClick} {...options}>
            {({ getRootProps, getInputProps }) => (
              <section>
                <div {...getRootProps({ className: classes.dropZone })}>
                  <input {...getInputProps({ id, ...options.inputProps })} />

                  {files.map((file, index) => (
                    <MyFileInputPreview key={index} file={file} onRemove={this.onRemove(file)} className={classes.chips} chipProps={chipProps} />
                  ))}
                  {files.length === 0 && <span>Drag and drop some files here, or click to select files</span>}
                </div>
                {meta && meta.touched && meta.error && <FormHelperText>{translate(meta.error)}</FormHelperText>}
              </section>
            )}
          </Dropzone>

          {/* <Dropzone
            onDrop={this.onDrop}
            accept={accept}
            disableClick={disableClick}
            maxSize={maxSize}
            minSize={minSize}
            multiple={multiple}
            className={classes.dropZone}
            {...options}
            inputProps={{ id, ...options.inputProps }}>
            <div className={files.length ? classes.preview : classes.paddingTop}>
              {files.map((file, index) => (
                <MyFileInputPreview key={index} file={file} onRemove={this.onRemove(file)} className={classes.chips} chipProps={chipProps} />
              ))}
              {files.length === 0 && <span>Drag and drop some files here, or click to select files</span>}
            </div>
            {meta && meta.touched && meta.error && <FormHelperText>{translate(meta.error)}</FormHelperText>}
          </Dropzone> */}
        </span>
      </Labeled>
    );
  }
}

export default compose(withFormField, translate, withStyles(styles))(MyFileInput);
